import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { TrendingUp, DollarSign, Calendar, Zap } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { StatCardSkeleton, CardSkeleton } from '../components/ui/skeleton';
import PredictiveHpaPanel from '../components/PredictiveHpaPanel';
import ForecastFloorPanel from '../components/ForecastFloorPanel';

function Forecasting() {
  const [clusters, setClusters] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState('');
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  // Forecast horizon — user-selectable (30/60/90). Backend clamps to [1, 365].
  const [forecastDays, setForecastDays] = useState(90);
  const [forecastLoading, setForecastLoading] = useState(false);
  // Universal predictive master switch for the selected cluster. null until
  // loaded; absent field / older backend is treated as ON (matches the backend
  // contract: no predictive_config -> master ON, features defer to their own
  // switches).
  const [masterEnabled, setMasterEnabled] = useState(null);
  const [masterSaving, setMasterSaving] = useState(false);

  useEffect(() => {
    fetchClusters();
  }, []);

  useEffect(() => {
    if (selectedCluster) {
      fetchForecast();
    }
  }, [selectedCluster, forecastDays]);

  useEffect(() => {
    if (selectedCluster) {
      fetchMaster();
    }
  }, [selectedCluster]);

  const fetchClusters = async () => {
    try {
      const response = await axios.get(`${API}/clusters`);
      setClusters(response.data);
      if (response.data.length > 0) {
        setSelectedCluster(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load clusters');
    } finally {
      setLoading(false);
    }
  };

  const fetchForecast = async () => {
    try {
      setForecastLoading(true);
      const response = await axios.get(`${API}/forecast/${selectedCluster}`, {
        params: { days: forecastDays },
      });
      setForecast(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load forecast');
      setForecast(null);
    } finally {
      setForecastLoading(false);
    }
  };

  const fetchMaster = async () => {
    try {
      const res = await axios.get(`${API}/clusters/${selectedCluster}/predictive/master`);
      setMasterEnabled(res.data.enabled !== false);
    } catch (error) {
      // Endpoint missing (older backend) or no predictive_config yet -> the
      // contract treats that as master ON. Stay quiet; don't error the page.
      console.log('Predictive master switch not available:', error);
      setMasterEnabled(true);
    }
  };

  const setMaster = async (enabled) => {
    const prev = masterEnabled;
    setMasterEnabled(enabled); // optimistic
    try {
      setMasterSaving(true);
      await axios.put(`${API}/clusters/${selectedCluster}/predictive/master`, { enabled });
      toast.success(
        enabled
          ? 'Predictive scaling enabled for this cluster'
          : 'Predictive scaling disabled for this cluster'
      );
      await fetchMaster();
    } catch (error) {
      setMasterEnabled(prev); // rollback
      toast.error(error.response?.data?.detail || 'Failed to update master switch');
    } finally {
      setMasterSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="skeleton h-8 w-48 rounded"></div>
          <div className="skeleton h-96 rounded-xl"></div>
        </div>
      </Layout>
    );
  }

  const selectedClusterData = clusters.find(c => c.id === selectedCluster);

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white">Cost Forecasting</h1>
            <p className="text-slate-600 dark:text-text-secondary mt-2 text-base sm:text-lg">
              Predict future costs based on historical data
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Select
              value={String(forecastDays)}
              onValueChange={(v) => setForecastDays(parseInt(v, 10))}
            >
              <SelectTrigger className="w-[140px]" data-testid="forecast-horizon-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">Next 30 days</SelectItem>
                <SelectItem value="60">Next 60 days</SelectItem>
                <SelectItem value="90">Next 90 days</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedCluster} onValueChange={setSelectedCluster}>
              <SelectTrigger className="w-[200px]" data-testid="cluster-select">
                <SelectValue placeholder="Select cluster" />
              </SelectTrigger>
              <SelectContent>
                {clusters.map((cluster) => (
                  <SelectItem key={cluster.id} value={cluster.id}>
                    {cluster.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!forecast ? (
          <Card>
            <CardContent className="py-16">
              <div className="text-center">
                <TrendingUp className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Not Enough Data for Forecasting
                </h3>
                <p className="text-slate-600">
                  Collect at least 5 metric snapshots to generate forecasts
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger-fade-in">
              <Card className="stat-card hover-lift transition-smooth border-0 shadow-lg" data-testid="forecast-period-card">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-600 mb-3">Forecast Period</p>
                      <p className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                        {forecast.forecast_period_days}
                      </p>
                      <p className="text-xs text-blue-600 mt-2 font-medium">days ahead</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl shadow-lg transition-transform hover:scale-110">
                      <Calendar className="w-7 h-7 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-zinc-200 dark:border-zinc-800 shadow-sm" data-testid="projected-cost-card">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-xs uppercase tracking-wide font-medium text-zinc-500 dark:text-zinc-400 mb-2">
                        Projected Total Cost
                      </p>
                      <p className="text-4xl font-semibold tracking-tight text-zinc-900 dark:text-white tabular-nums">
                        ${(forecast.projected_total_cost ?? forecast.projected_90_day_cost)?.toFixed(0)}
                      </p>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-3">
                        sum of {forecast.forecast_period_days} days · {forecast.confidence || '—'} confidence
                      </p>
                    </div>
                    <div className="p-2 rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                      <DollarSign className="w-5 h-5" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-zinc-200 dark:border-zinc-800 shadow-sm" data-testid="avg-daily-cost-card">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-xs uppercase tracking-wide font-medium text-zinc-500 dark:text-zinc-400 mb-2">
                        Avg Daily Forecast
                      </p>
                      <p className="text-4xl font-semibold tracking-tight text-zinc-900 dark:text-white tabular-nums">
                        ${(forecast.cost_breakdown?.daily_average ?? ((forecast.projected_total_cost ?? forecast.projected_90_day_cost) / (forecast.forecast_period_days || 90)))?.toFixed(2)}
                      </p>
                      <p className={`text-xs mt-3 ${
                        forecast.trend_direction === 'up' ? 'text-amber-600 dark:text-amber-500' :
                        forecast.trend_direction === 'down' ? 'text-emerald-600 dark:text-emerald-500' :
                        'text-zinc-500 dark:text-zinc-400'
                      }`}>
                        {forecast.trend_direction === 'up'
                          ? `↑ trending up ${(forecast.trend_per_day || 0).toFixed(2)}/day`
                          : forecast.trend_direction === 'down'
                            ? `↓ trending down ${Math.abs(forecast.trend_per_day || 0).toFixed(2)}/day`
                            : 'flat (no trend detected)'}
                      </p>
                    </div>
                    <div className="p-2 rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                      <TrendingUp className="w-5 h-5" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Forecast Chart */}
            <Card data-testid="forecast-chart">
              <CardHeader>
                <CardTitle className="text-lg">
                  {forecast.forecast_period_days}-Day Cost Forecast — {selectedClusterData?.name}
                  {forecastLoading && <span className="ml-2 text-xs font-normal text-slate-400">updating…</span>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={500}>
                  <AreaChart data={forecast.forecast} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.4} />
                        <stop offset="50%" stopColor="#a78bfa" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#c4b5fd" stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="forecastBandGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#c4b5fd" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#c4b5fd" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.5} />
                    <XAxis
                      dataKey="date"
                      stroke="#94a3b8"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => `$${value}`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(15, 23, 42, 0.96)',
                        border: 'none',
                        borderRadius: '16px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                        padding: '16px 20px',
                        backdropFilter: 'blur(12px)'
                      }}
                      labelStyle={{
                        color: '#f1f5f9',
                        fontWeight: '700',
                        marginBottom: '12px',
                        fontSize: '13px',
                        letterSpacing: '0.5px'
                      }}
                      itemStyle={{
                        color: '#e2e8f0',
                        fontSize: '14px',
                        fontWeight: '500',
                        padding: '4px 0'
                      }}
                      cursor={{ stroke: '#8b5cf6', strokeWidth: 2, strokeDasharray: '5 5', opacity: 0.5 }}
                      labelFormatter={(value) => new Date(value).toLocaleDateString()}
                      formatter={(value) => [`$${value.toFixed(2)}`, 'Estimated Cost']}
                    />
                    {/* Confidence band — rendered as upper-band Area then
                        lower-band Area in matching background colour so the
                        visual is the difference between the two. Recharts
                        renders later Areas on top, so the estimated line
                        Area below this is the visible point estimate. */}
                    <Area
                      type="monotone"
                      dataKey="confidence_upper"
                      stroke="none"
                      fill="url(#forecastBandGradient)"
                      dot={false}
                      activeDot={false}
                      isAnimationActive={false}
                    />
                    <Area
                      type="monotone"
                      dataKey="confidence_lower"
                      stroke="none"
                      fill="#ffffff"
                      fillOpacity={1}
                      dot={false}
                      activeDot={false}
                      isAnimationActive={false}
                    />
                    <Area
                      type="monotone"
                      dataKey="estimated_cost"
                      stroke="#8b5cf6"
                      strokeWidth={2.5}
                      fill="url(#forecastGradient)"
                      dot={false}
                      activeDot={false}
                      animationDuration={1500}
                      animationBegin={0}
                      animationEasing="ease-in-out"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Weekly Breakdown — adapts to horizon: 30d → ~5 weeks,
                60d → ~9 weeks, 90d → ~13 weeks. */}
            <Card data-testid="weekly-breakdown">
              <CardHeader>
                <CardTitle className="text-lg">Weekly Cost Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Array.from({ length: Math.ceil((forecast.forecast?.length || 0) / 7) }, (_, week) => {
                    const startIdx = week * 7;
                    const endIdx = Math.min(startIdx + 7, forecast.forecast.length);
                    const weekData = forecast.forecast.slice(startIdx, endIdx);
                    if (weekData.length === 0) return null;
                    const weekTotal = weekData.reduce((sum, day) => sum + day.estimated_cost, 0);
                    const startDate = new Date(weekData[0]?.date).toLocaleDateString();
                    const endDate = new Date(weekData[weekData.length - 1]?.date).toLocaleDateString();
                    const dayCount = weekData.length;

                    return (
                      <div key={week} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-surface-border rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">
                            Week {week + 1}
                            {dayCount < 7 && <span className="text-xs text-slate-500 dark:text-text-secondary ml-1">({dayCount} days)</span>}
                          </p>
                          <p className="text-sm text-slate-600 dark:text-text-secondary">{startDate} - {endDate}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">${weekTotal.toFixed(2)}</p>
                          <p className="text-xs text-slate-500 dark:text-text-secondary">${(weekTotal / 7).toFixed(2)}/day avg</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Universal predictive master switch — governs BOTH panels below. When
            off, neither feature emits new actions, but in-flight reverts and
            grading still complete. Rendered above both panels, cluster-scoped. */}
        {selectedCluster && (
          <Card
            data-testid="predictive-master-card"
            className="border border-zinc-200 dark:border-zinc-800 shadow-sm"
          >
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-md bg-purple-100 dark:bg-purple-950 text-purple-600 dark:text-purple-400 shrink-0">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-slate-900 dark:text-white">
                      Predictive scaling — master switch
                    </p>
                    <p className="text-sm text-slate-500 dark:text-text-secondary mt-1 max-w-2xl">
                      Turns ALL predictive features (HPA pre-scaling + forecast-driven requests) on
                      or off for this cluster. When off, in-flight reverts and grading still complete.
                    </p>
                  </div>
                </div>
                <Switch
                  checked={masterEnabled !== false}
                  onCheckedChange={setMaster}
                  disabled={masterSaving || masterEnabled === null}
                  className="scale-125 sm:mr-2"
                  data-testid="predictive-master-toggle"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feature 3 — predictive HPA pre-scaling controls + forecast-vs-actual.
            Independent of the cost forecast above, so it renders whenever a
            cluster is selected. */}
        {selectedCluster && <PredictiveHpaPanel clusterId={selectedCluster} />}

        {/* Feature 4 — forecast-floor (predictive CPU/mem request pre-raise) opt-in. */}
        {selectedCluster && <ForecastFloorPanel clusterId={selectedCluster} />}
      </div>
    </Layout>
  );
}

export default Forecasting;